/* The chart behind a Home tile's caret (UX-007 §9).
   ═══════════════════════════════════════════════════════════════════════════

   The caret opens the figure's own history IN PLACE. It is a disclosure, not a
   link: nothing navigates, and the tiles stay on screen above the chart.

   What is drawn follows the WINDOW, because the platform holds two different
   kinds of record and they do not mix:

     * ONE DAY  -> telemetry, five-minute, clock on the x axis. The live day is
       served per site from the time-series store, so a tile that names a
       measured signal (power, the daily energy register) draws one line per
       site over that day.

     * MANY DAYS -> the KPI engine's daily figures, DATE on the x axis. A day the
       engine produced nothing for keeps its place and is ticked, never zeroed
       (the whole point of KpiTrend).

   ONE LINE. A fleet of a hundred sites cannot be a hundred lines, so the panel
   asks for the FLEET figure and draws one. The server does the rolling up: an
   additive signal sums across sites, and a ratio is one division of summed terms
   (ruling §4.2), which is the same roll-up the live day has always served. Where
   the server refuses a roll-up — because its terms are not stored, as for the
   availabilities — the panel says so in the server's own words rather than
   averaging anything itself. Nothing here computes a KPI (CLAUDE.md rule 2).

   READING A VALUE. A long window cannot label every point, so the chart is
   scrubbable: the position under the pointer and its figure are printed in words
   above the plot, with a crosshair on the line.
*/
import {
  getGetV1KpiSeriesQueryOptions,
  getGetV1SeriesQueryOptions,
  type GetV1KpiSeriesMetric,
} from "@solarkey/api-client";
import { KpiTrend, type TrendLine } from "@solarkey/charts";
import { StateWord, atSeriesScale, reasonText, seriesDisplayScale } from "@solarkey/ui";
import { useQueries } from "@tanstack/react-query";
import type { ReactNode } from "react";

/** The tiles the drawing puts a caret on. */
export type TileKey =
  | "active_power" | "energy_today" | "pr"
  | "availability" | "sites_reporting" | "alerts_open";

interface Spec {
  title: string;
  /** Telemetry signal for a single day, read per site. */
  signal?: string;
  /** The engine's daily figure for a multi-day window. */
  metric?: GetV1KpiSeriesMetric;
  /** True when the server rolls this up to the fleet — a sum for an additive
   *  quantity, a ratio of summed terms for PR. False only where the terms the
   *  roll-up needs are not stored, which is where the panel says so instead. */
  sums: boolean;
  /** Said when the multi-day chart is not the tile's own quantity. */
  note?: string;
  /** Said instead of a chart when one day holds no curve for this tile. */
  dayNote?: string;
  /** What the multi-day chart is actually of, when it is not `title`. */
  rangeTitle?: string;
}

const SPEC: Record<TileKey, Spec> = {
  active_power: {
    title: "Active power", signal: "ac_active_power", metric: "energy", sums: true,
    // Power is an instantaneous reading; the engine has no daily "power" figure.
    // Over several days its own daily record of the same thing is energy, and the
    // chart is labelled energy — a basis is never relabelled as another (§4.2).
    rangeTitle: "Energy per day",
    note: "Power is instantaneous, so it has no daily figure. Over a range the engine's own daily record is energy.",
  },
  energy_today: {
    // The daily register's first interval of the local day still carries the
    // previous day's close. The SERVER now withholds those readings with their
    // reason instead of serving them, so the curve starts where the day does.
    title: "Energy", signal: "energy_active_daily", metric: "energy", sums: true,
  },
  pr: { title: "PR", metric: "pr", sums: true },
  availability: { title: "Plant availability", metric: "plant_availability", sums: false },
  sites_reporting: { title: "Reporting availability", metric: "reporting_availability", sums: false },
  alerts_open: { title: "Alerts", sums: false },
};

/** The hours a single-day axis runs: the solar day, not midnight to midnight.
 *  The same window the fleet chart below the tiles draws. */
const DAY_FIRST_HOUR = 6;
const DAY_LAST_HOUR = 19;

/** HH:MM in the viewer's own zone — the same way a time axis is drawn elsewhere.
 *  Never a hardcoded zone (rule 3). */
function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

type Pt = { ts?: string; day?: string; value?: number | null; figure?: { value?: number | null } };

/** One site's points, keyed by x, as the chart wants them. */
function indexPoints(points: Pt[], key: "ts" | "day"): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const p of points) {
    const k = key === "ts" ? p.ts : p.day;
    if (!k) continue;
    const v = key === "ts" ? p.value : p.figure?.value;
    out.set(k, typeof v === "number" ? v : null);
  }
  return out;
}

export function TileTrend({ tile, sites, win, onClose }: {
  tile: TileKey;
  /** The site slugs in scope, in the order the fleet table shows them. */
  sites: string[];
  /** The window the SERVER resolved: inclusive local dates plus half-open instants. */
  win?: { start?: string; end?: string; start_at?: string; end_at?: string };
  onClose: () => void;
}): ReactNode {
  const spec = SPEC[tile];
  const oneDay = Boolean(win?.start && win.start === win.end);

  // ONE request, one line. The roll-up is the server's: `scope=all` sums an
  // additive telemetry signal across sites and divides a ratio once over summed
  // terms. The per-site fan-out is kept ONLY for the metrics whose terms are not
  // stored, where a fleet figure would have to be invented — and even then the
  // server states why, so the fan-out is a courtesy, not a substitute.
  const scopes = spec.sums ? ["all"] : sites;

  const telemetry = useQueries({
    queries: oneDay && spec.signal
      ? scopes.map((sc) => getGetV1SeriesQueryOptions(
          sc === "all"
            ? { scope: "all", signal: spec.signal as string,
                from: win?.start_at ?? "", to: win?.end_at ?? "" }
            : { site: sc, signal: spec.signal as string,
                from: win?.start_at ?? "", to: win?.end_at ?? "" },
          { query: { enabled: Boolean(win?.start_at && win?.end_at) } },
        ))
      : [],
  });

  const daily = useQueries({
    queries: !oneDay && spec.metric
      ? scopes.map((s) => getGetV1KpiSeriesQueryOptions(
          { metric: spec.metric as GetV1KpiSeriesMetric, scope: s,
            period: "custom", from: win?.start, to: win?.end },
          { query: { enabled: Boolean(win?.start && win?.end) } },
        ))
      : [],
  });

  const active = oneDay ? telemetry : daily;
  const loading = active.some((q) => q.isLoading);

  // Nothing to draw at all for this tile.
  if (!spec.signal && !spec.metric) {
    return (
      <Panel title={spec.title} onClose={onClose}>
        <p className="muted" style={{ fontSize: 11, margin: 0 }}>
          This tile counts open alerts. A count has no engine-computed history,
          so there is no trend to draw — the Alerts screen holds the record.
        </p>
      </Panel>
    );
  }
  if (oneDay && !spec.signal) {
    return (
      <Panel title={spec.title} onClose={onClose}>
        <p className="muted" style={{ fontSize: 11, margin: 0 }}>
          {spec.dayNote ?? `${spec.title} is a daily figure: the engine computes it `
            + "once per completed day, so a single day holds one point and no curve. "
            + "Choose 7D or 30D to see it over time."}
        </p>
      </Panel>
    );
  }

  // A single-day axis runs the solar day. `win.start_at` is the server's resolved
  // window start, which on one day IS local midnight, so the bounds are that
  // instant plus a fixed number of hours: no browser clock, no guessed timezone.
  const dayStartMs = oneDay && win?.start_at ? Date.parse(win.start_at) : NaN;
  const inSolarDay = (iso: string): boolean => {
    if (!Number.isFinite(dayStartMs)) return true;
    const h = (Date.parse(iso) - dayStartMs) / 3_600_000;
    return h >= DAY_FIRST_HOUR && h <= DAY_LAST_HOUR;
  };

  const key = oneDay ? "ts" : "day";
  const indexed = active.map((q) => {
    const d = q.data?.status === 200
      ? (q.data.data.data as unknown as { points?: Pt[]; unit?: string;
                                          reason?: Record<string, unknown> })
      : undefined;
    return { points: indexPoints(d?.points ?? [], key), unit: d?.unit, reason: d?.reason };
  });

  // The x axis is the UNION of every scope's own positions, in order, so one
  // site's gap never shortens the axis for the others.
  const xs = [...new Set(indexed.flatMap((i) => [...i.points.keys()]))]
    .filter((x) => (oneDay ? inSolarDay(x) : true))
    .sort();

  const raw = indexed.map((i) => xs.map((x) => i.points.get(x) ?? null));
  // One scale decision for the whole axis, by the same rule the tiles use, so a
  // fleet day reads in MWh rather than six digits of kWh (which also ran the axis
  // labels off the left edge of the plot).
  const sc = seriesDisplayScale(indexed.find((i) => i.unit)?.unit ?? undefined, raw);

  // Several sites are PEERS in the grammar (UX-006 §4): comparable entities drawn
  // in one role, not a hierarchy. A single scope is the measured line.
  const lines: TrendLine[] = raw.map((values, n) => ({
    label: scopes[n] === "all" ? "fleet" : (scopes[n] as string),
    kind: scopes.length === 1 ? "measured" : "peer",
    values: atSeriesScale(values, sc.factor),
  }));

  const drawn = lines.some((l) => l.values.some((v) => typeof v === "number"));
  const withheld = xs.length - xs.filter(
    (_, n) => lines.some((l) => typeof l.values[n] === "number")).length;

  return (
    <Panel title={!oneDay && spec.rangeTitle ? spec.rangeTitle : spec.title}
           onClose={onClose}
           sub={oneDay ? "today · 5-minute" : `${win?.start} – ${win?.end} · daily`}>
      {spec.note && !oneDay ? (
        <p className="muted" style={{ fontSize: 10, margin: "0 0 6px" }}>{spec.note}</p>
      ) : null}

      {loading ? (
        <div className="muted" style={{ fontSize: 11 }}>Loading…</div>
      ) : !drawn ? (
        // Never an empty frame: the server said why, so the panel says why.
        <div style={{ fontSize: 11 }}>
          <StateWord state="CANNOT_CHECK"
                     reason={(indexed.find((i) => i.reason)?.reason as never)
                             ?? { template: "CANNOT_CHECK_NO_SERIES_FOR_WINDOW" }} />
          {indexed.find((i) => i.reason) ? (
            <div className="muted" style={{ marginTop: 4 }}>
              {reasonText(indexed.find((i) => i.reason)?.reason as never)}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <KpiTrend
            unit={sc.unit}
            days={xs}
            formatX={oneDay ? clock : undefined}
            lines={lines}
            height={160}
            ariaLabel={`${spec.title} over ${oneDay ? "the day" : "the window"}`}
            summary={`${spec.title}: ${xs.length - withheld} of ${xs.length} positions carry a figure.`}
          />
          <div className="sk-chart-legend" style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px" }}>
            {lines.map((l) => (
              <span key={l.label} className="sk-chart-legend__item"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span className="sk-chart-legend__swatch"
                      style={{ width: 10, height: 2, display: "inline-block",
                               background: l.kind === "measured"
                                 ? "var(--sk-color-ink-1)" : "var(--sk-color-ink-3)" }} />
                {l.label}
              </span>
            ))}
          </div>
          {!spec.sums && scopes.length > 1 ? (
            <p className="muted" style={{ fontSize: 10, margin: "4px 0 0" }}>
              One line per site. {spec.title} is a ratio, and a ratio has no fleet
              roll-up: averaging per-site ratios is not the fleet's ratio, so the
              engine's own per-site figures are drawn instead.
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

function Panel({ title, sub, onClose, children }: {
  title: string; sub?: string; onClose: () => void; children: ReactNode;
}): ReactNode {
  return (
    <div className="trend" style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <h4 style={{ margin: 0 }}>{title}</h4>
        {sub ? <span className="sub muted">{sub}</span> : null}
        <button type="button" className="chip" style={{ marginLeft: "auto" }}
                onClick={onClose}>Close</button>
      </div>
      {children}
    </div>
  );
}
